import { useEffect, type ReactNode } from "react";
import t from "@/lang/lang";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import {
  ChangeOfferDocumentProfilePatch,
  CreateOfferDocumentProfile,
  OfferDocumentProfile,
} from "@/services/backend/qpm/offers/types/offer";
import { DateInput } from "@/shared/components/domain/qpm/inputs/date-input";
import {
  ChangeMode,
  ConfigOrigin,
  ExtensionAreaScope,
  IoTTableMode,
  MainOfferKind,
  OfferType,
  PriorIoTPresenceState,
  SectionMode,
  TemplateScenario,
} from "@/services/backend/qpm/offers/types/offer-enum";
import {
  Editor,
  EditorBoldItalicUnderlineGroup,
  EditorBulletList,
  EditorContent,
  editorGenerateHTML,
  EditorOrderedList,
  EditorToolbar,
  EditorToolbarSeparator,
  useEditor,
} from "@/shared/components/editor/editor";
import {
  TargetOrganismSelectionEditor,
  type TargetOrganismSelectionValue,
} from "@/shared/components/domain/qpm/target-organism-selection-editor";

export type OfferDocumentProfileDraft = {
  offerType: OfferType;
  mainOfferKind: MainOfferKind;
  priorIoTPresenceState: PriorIoTPresenceState;
  tableMode: SectionMode;
  ioTInTableMode: IoTTableMode;
  ioTRentalContractMode: SectionMode;
  referencedContractDate: string | null;
  contractAgainstText: string;
  targetAreaScope: ExtensionAreaScope;
  templateSetVersion?: string;
};

export type DocumentProfilePatchField =
  | "offerType"
  | "mainOfferKind"
  | "priorIoTPresenceState"
  | "tableMode"
  | "ioTInTableMode"
  | "ioTRentalContractMode"
  | "contractAgainstText"
  | "targetAreaScope";

export type DocumentProfileFieldModes = Partial<
  Record<DocumentProfilePatchField, ChangeMode>
>;

export function createDefaultOfferDocumentProfileDraft(): OfferDocumentProfileDraft {
  return {
    offerType: OfferType.Main,
    mainOfferKind: MainOfferKind.Unspecified,
    priorIoTPresenceState: PriorIoTPresenceState.Unknown,
    tableMode: SectionMode.Include,
    ioTInTableMode: IoTTableMode.Exclude,
    ioTRentalContractMode: SectionMode.Omit,
    referencedContractDate: null,
    contractAgainstText: "",
    targetAreaScope: ExtensionAreaScope.Indoor,
    templateSetVersion: undefined,
  };
}

export function createOfferDocumentProfileDraftFromOffer(
  profile?: OfferDocumentProfile | null,
): OfferDocumentProfileDraft {
  if (!profile) {
    return createDefaultOfferDocumentProfileDraft();
  }

  return {
    offerType: profile.offerType?.value ?? OfferType.Main,
    mainOfferKind: profile.mainOfferKind?.value ?? MainOfferKind.Unspecified,
    priorIoTPresenceState:
      profile.extension?.priorIoTPresenceState?.value ??
      PriorIoTPresenceState.Unknown,
    tableMode: profile.extension?.tableMode?.value ?? SectionMode.Include,
    ioTInTableMode:
      profile.extension?.ioTInTableMode?.value ?? IoTTableMode.Exclude,
    ioTRentalContractMode:
      profile.extension?.ioTRentalContractMode?.value ?? SectionMode.Omit,
    referencedContractDate: profile.extension?.referencedContractDate ?? null,
    contractAgainstText: profile.extension?.contractAgainstText?.value ?? "",
    targetAreaScope:
      profile.extension?.targetAreaScope?.value ?? ExtensionAreaScope.Indoor,
    templateSetVersion: profile.templateSetVersion,
  };
}

export function validateOfferDocumentProfileDraft(
  draft: OfferDocumentProfileDraft,
): string | null {
  if (
    draft.offerType === OfferType.Extension &&
    draft.tableMode === SectionMode.Omit &&
    draft.ioTRentalContractMode === SectionMode.Omit
  ) {
    return t(
      "Bei Erweiterungsangeboten muss mindestens Tabelle anzeigen oder IoT-Mietvertrag anzeigen aktiv sein.",
    );
  }

  if (
    draft.offerType === OfferType.Extension &&
    draft.tableMode !== SectionMode.Include &&
    draft.ioTInTableMode === IoTTableMode.Include
  ) {
    return t("IoT in Tabelle ist nur aktiv, wenn Tabelle anzeigen aktiv ist.");
  }

  return null;
}

export function buildCreateOfferDocumentProfile(
  draft: OfferDocumentProfileDraft,
): CreateOfferDocumentProfile {
  const profile: CreateOfferDocumentProfile = {
    offerType: {
      origin: ConfigOrigin.Manual,
      value: draft.offerType,
    },
    templateSetVersion: draft.templateSetVersion,
  };

  if (draft.offerType === OfferType.Main) {
    profile.mainOfferKind = {
      origin: ConfigOrigin.Manual,
      value: draft.mainOfferKind,
    };
  }

  if (draft.offerType === OfferType.Extension) {
    profile.priorIoTPresenceState = {
      origin: ConfigOrigin.Manual,
      value: draft.priorIoTPresenceState,
    };
    profile.tableMode = {
      origin: ConfigOrigin.Manual,
      value: draft.tableMode,
    };
    if (draft.tableMode === SectionMode.Include) {
      profile.ioTInTableMode = {
        origin: ConfigOrigin.Manual,
        value: draft.ioTInTableMode,
      };
    }
    profile.ioTRentalContractMode = {
      origin: ConfigOrigin.Manual,
      value: draft.ioTRentalContractMode,
    };
    profile.referencedContractDate = draft.referencedContractDate;
    profile.contractAgainstText = {
      origin: ConfigOrigin.Manual,
      value: draft.contractAgainstText,
    };
    profile.targetAreaScope = {
      origin: ConfigOrigin.Manual,
      value: draft.targetAreaScope,
    };
  }

  return profile;
}

export function createPatchFromCreateProfile(
  profile: CreateOfferDocumentProfile,
): ChangeOfferDocumentProfilePatch {
  const patch: ChangeOfferDocumentProfilePatch = {
    offerType: { mode: ChangeMode.Manual, value: profile.offerType.value },
  };

  if (profile.mainOfferKind) {
    patch.mainOfferKind = {
      mode: ChangeMode.Manual,
      value: profile.mainOfferKind.value,
    };
  }
  if (profile.priorIoTPresenceState) {
    patch.priorIoTPresenceState = {
      mode: ChangeMode.Manual,
      value: profile.priorIoTPresenceState.value,
    };
  }
  if (profile.tableMode) {
    patch.tableMode = {
      mode: ChangeMode.Manual,
      value: profile.tableMode.value,
    };
  }
  if (profile.ioTInTableMode) {
    patch.ioTInTableMode = {
      mode: ChangeMode.Manual,
      value: profile.ioTInTableMode.value,
    };
  }
  if (profile.ioTRentalContractMode) {
    patch.ioTRentalContractMode = {
      mode: ChangeMode.Manual,
      value: profile.ioTRentalContractMode.value,
    };
  }
  if (profile.contractAgainstText) {
    patch.contractAgainstText = {
      mode: ChangeMode.Manual,
      value: profile.contractAgainstText.value,
    };
  }
  if (profile.targetAreaScope) {
    patch.targetAreaScope = {
      mode: ChangeMode.Manual,
      value: profile.targetAreaScope.value,
    };
  }
  if (profile.referencedContractDate !== undefined) {
    patch.referencedContractDate = profile.referencedContractDate;
  }

  return patch;
}

export function buildChangeOfferDocumentProfilePatch(
  initial: OfferDocumentProfileDraft,
  current: OfferDocumentProfileDraft,
  fieldModes: DocumentProfileFieldModes,
): ChangeOfferDocumentProfilePatch {
  const patch: ChangeOfferDocumentProfilePatch = {};

  assignPatchField(
    patch,
    "offerType",
    fieldModes,
    initial.offerType,
    current.offerType,
  );

  if (current.offerType === OfferType.Main) {
    assignPatchField(
      patch,
      "mainOfferKind",
      fieldModes,
      initial.mainOfferKind,
      current.mainOfferKind,
    );
    return patch;
  }

  assignPatchField(
    patch,
    "priorIoTPresenceState",
    fieldModes,
    initial.priorIoTPresenceState,
    current.priorIoTPresenceState,
  );
  assignPatchField(
    patch,
    "tableMode",
    fieldModes,
    initial.tableMode,
    current.tableMode,
  );
  if (current.tableMode === SectionMode.Include) {
    assignPatchField(
      patch,
      "ioTInTableMode",
      fieldModes,
      initial.ioTInTableMode,
      current.ioTInTableMode,
    );
  }
  assignPatchField(
    patch,
    "ioTRentalContractMode",
    fieldModes,
    initial.ioTRentalContractMode,
    current.ioTRentalContractMode,
  );
  assignPatchField(
    patch,
    "contractAgainstText",
    fieldModes,
    initial.contractAgainstText,
    current.contractAgainstText,
  );
  assignPatchField(
    patch,
    "targetAreaScope",
    fieldModes,
    initial.targetAreaScope,
    current.targetAreaScope,
  );

  const initialDate = normalizeDate(initial.referencedContractDate);
  const currentDate = normalizeDate(current.referencedContractDate);
  if (currentDate !== initialDate) {
    patch.referencedContractDate = currentDate;
  }

  return patch;
}

function assignPatchField<T extends string | number>(
  patch: ChangeOfferDocumentProfilePatch,
  field: DocumentProfilePatchField,
  fieldModes: DocumentProfileFieldModes,
  initialValue: T,
  currentValue: T,
) {
  const target = patch as Record<
    DocumentProfilePatchField,
    { mode: ChangeMode; value?: string | number } | undefined
  >;

  if (fieldModes[field] === ChangeMode.Automatic) {
    target[field] = { mode: ChangeMode.Automatic };
    return;
  }

  if (currentValue !== initialValue) {
    target[field] = { mode: ChangeMode.Manual, value: currentValue };
  }
}

function normalizeDate(date: string | null | undefined): string | null {
  if (!date || date.trim() === "") {
    return null;
  }

  return date;
}

export function offerTypeLabel(offerType: OfferType): string {
  return offerType === OfferType.Extension
    ? t("Erweiterungsangebot")
    : t("Hauptangebot");
}

export function templateScenarioLabel(scenario: TemplateScenario): string {
  return t(scenario);
}

const AUTOMATIC_SELECT_VALUE = "__automatic__";

function automaticLabel(valueLabel: string): string {
  return `${valueLabel} (${t("automatisch bestimmt")})`;
}

function mainOfferKindLabel(value: MainOfferKind): string {
  switch (value) {
    case MainOfferKind.FirstOffer:
      return t("Erstangebot");
    case MainOfferKind.NewContract:
      return t("Neuvertrag");
    default:
      return t("Nicht festgelegt");
  }
}

function priorIoTPresenceStateLabel(value: PriorIoTPresenceState): string {
  switch (value) {
    case PriorIoTPresenceState.Present:
      return t("Vorhanden");
    case PriorIoTPresenceState.Absent:
      return t("Nicht vorhanden");
    default:
      return t("Unbekannt");
  }
}

function sectionModeLabel(value: SectionMode): string {
  return value === SectionMode.Include ? t("Anzeigen") : t("Ausblenden");
}

function ioTTableModeLabel(value: IoTTableMode): string {
  return value === IoTTableMode.Include ? t("Anzeigen") : t("Ausblenden");
}

function extensionAreaScopeLabel(value: ExtensionAreaScope): string {
  switch (value) {
    case ExtensionAreaScope.Outdoor:
      return t("Außenbereich");
    case ExtensionAreaScope.IndoorOutdoor:
      return t("Innen- und Außenbereich");
    default:
      return t("Innenbereich");
  }
}

function ContractAgainstHtmlEditor({
  value,
  disabled,
  onChange,
}: {
  value: string;
  disabled?: boolean;
  onChange: (next: string) => void;
}) {
  const targetValue = value?.trim() ? value : "<p></p>";
  const editor = useEditor(
    {
      content: value,
      editable: !disabled,
      onUpdate: ({ editor: currentEditor }) => {
        onChange(editorGenerateHTML(currentEditor));
      },
    },
    [disabled],
  );

  useEffect(() => {
    if (!editor) {
      return;
    }
    const currentHTML = editorGenerateHTML(editor).trim() || "<p></p>";
    if (currentHTML === targetValue) {
      return;
    }
    if (!disabled && editor.isFocused) {
      return;
    }
    editor.commands.setContent(targetValue, { emitUpdate: false });
  }, [disabled, editor, targetValue]);

  if (!editor) {
    return <Input value={value} disabled readOnly />;
  }

  return (
    <Editor editor={editor}>
      <EditorToolbar>
        <EditorBoldItalicUnderlineGroup />
        <EditorToolbarSeparator />
        <EditorBulletList />
        <EditorOrderedList />
      </EditorToolbar>
      <EditorContent />
    </Editor>
  );
}

export function OfferDocumentProfileForm({
  mode,
  value,
  onChange,
  disabled,
  fieldModes,
  onFieldModeChange,
  targetOrganismSelection,
  onTargetOrganismSelectionChange,
  showTargetOrganismSelection = true,
  showPestDisplayFields = true,
  showAllFields = false,
  validationMessage,
}: {
  mode: "create" | "patch";
  value: OfferDocumentProfileDraft;
  onChange: (next: OfferDocumentProfileDraft) => void;
  disabled?: boolean;
  fieldModes?: DocumentProfileFieldModes;
  onFieldModeChange?: (
    field: DocumentProfilePatchField,
    mode: ChangeMode,
  ) => void;
  targetOrganismSelection?: TargetOrganismSelectionValue;
  onTargetOrganismSelectionChange?: (
    next: TargetOrganismSelectionValue,
  ) => void;
  showTargetOrganismSelection?: boolean;
  showPestDisplayFields?: boolean;
  showAllFields?: boolean;
  validationMessage?: string | null;
}) {
  const isExtension = value.offerType === OfferType.Extension;
  const showMainOfferFields = showAllFields || !isExtension;
  const showExtensionFields = showAllFields || isExtension;
  const canEditIotInTable =
    isExtension && value.tableMode === SectionMode.Include;

  const update = (next: Partial<OfferDocumentProfileDraft>) => {
    const merged = { ...value, ...next };

    if (merged.offerType === OfferType.Main) {
      merged.priorIoTPresenceState = PriorIoTPresenceState.Unknown;
      merged.tableMode = SectionMode.Include;
      merged.ioTInTableMode = IoTTableMode.Exclude;
      merged.ioTRentalContractMode = SectionMode.Omit;
      merged.referencedContractDate = null;
      merged.contractAgainstText = "";
      merged.targetAreaScope = ExtensionAreaScope.Indoor;
    } else {
      merged.mainOfferKind = MainOfferKind.Unspecified;
    }

    if (merged.tableMode !== SectionMode.Include) {
      merged.ioTInTableMode = IoTTableMode.Exclude;
    }

    if (JSON.stringify(merged) === JSON.stringify(value)) {
      return;
    }

    onChange(merged);
  };

  const markManual = (field: DocumentProfilePatchField) => {
    if (mode === "patch") {
      onFieldModeChange?.(field, ChangeMode.Manual);
    }
  };

  const isAutomatic = (field: DocumentProfilePatchField) =>
    mode === "patch" && fieldModes?.[field] === ChangeMode.Automatic;

  const shouldRenderManualOption = <T extends string>(
    field: DocumentProfilePatchField,
    derivedValue: T,
    optionValue: T,
  ) => !(isAutomatic(field) && derivedValue === optionValue);

  const withOverrideMode = (
    field: DocumentProfilePatchField,
    control: ReactNode,
  ) => {
    if (mode !== "patch") {
      return control;
    }

    return (
      <div className="grid gap-2 md:grid-cols-[160px_minmax(0,1fr)]">
        <Select
          value={isAutomatic(field) ? ChangeMode.Automatic : ChangeMode.Manual}
          disabled={disabled}
          onValueChange={(next) =>
            onFieldModeChange?.(field, next as ChangeMode)
          }
        >
          <SelectTrigger className="h-9 w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ChangeMode.Manual}>{t("Manuell")}</SelectItem>
            <SelectItem value={ChangeMode.Automatic}>
              {t("Automatisch")}
            </SelectItem>
          </SelectContent>
        </Select>
        {control}
      </div>
    );
  };

  const row = (label: string, control: ReactNode, rowClassName?: string) => (
    <div className={rowClassName ?? "grid content-start gap-1.5"}>
      <Label className="min-h-5 text-sm">{label}</Label>
      <div>{control}</div>
    </div>
  );

  return (
    <div className="space-y-3">
      <div className="grid gap-3 md:grid-cols-2">
        {row(
          t("Angebotsart"),
          <Select
            value={value.offerType}
            disabled={disabled}
            onValueChange={(next) => {
              markManual("offerType");
              update({ offerType: next as OfferType });
            }}
          >
            <SelectTrigger className="h-9 w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={OfferType.Main}>
                {t("Hauptangebot")}
              </SelectItem>
              <SelectItem value={OfferType.Extension}>
                {t("Erweiterungsangebot")}
              </SelectItem>
            </SelectContent>
          </Select>,
        )}

        {showMainOfferFields &&
          row(
            t("Art des Hauptangebots"),
            <Select
              value={
                isAutomatic("mainOfferKind")
                  ? AUTOMATIC_SELECT_VALUE
                  : value.mainOfferKind
              }
              disabled={Boolean(disabled || isExtension)}
              onValueChange={(next) => {
                if (next === AUTOMATIC_SELECT_VALUE) {
                  onFieldModeChange?.("mainOfferKind", ChangeMode.Automatic);
                  return;
                }
                markManual("mainOfferKind");
                update({ mainOfferKind: next as MainOfferKind });
              }}
            >
              <SelectTrigger className="h-9 w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {mode === "patch" && (
                  <SelectItem value={AUTOMATIC_SELECT_VALUE}>
                    {automaticLabel(mainOfferKindLabel(value.mainOfferKind))}
                  </SelectItem>
                )}
                {shouldRenderManualOption(
                  "mainOfferKind",
                  value.mainOfferKind,
                  MainOfferKind.Unspecified,
                ) && (
                  <SelectItem value={MainOfferKind.Unspecified}>
                    {t("Nicht festgelegt")}
                  </SelectItem>
                )}
                {shouldRenderManualOption(
                  "mainOfferKind",
                  value.mainOfferKind,
                  MainOfferKind.FirstOffer,
                ) && (
                  <SelectItem value={MainOfferKind.FirstOffer}>
                    {t("Erstangebot")}
                  </SelectItem>
                )}
                {shouldRenderManualOption(
                  "mainOfferKind",
                  value.mainOfferKind,
                  MainOfferKind.NewContract,
                ) && (
                  <SelectItem value={MainOfferKind.NewContract}>
                    {t("Neuvertrag")}
                  </SelectItem>
                )}
              </SelectContent>
            </Select>,
          )}

        {showExtensionFields &&
          row(
            t("Vorheriger IoT-Bestand"),
            <Select
              value={
                isAutomatic("priorIoTPresenceState")
                  ? AUTOMATIC_SELECT_VALUE
                  : value.priorIoTPresenceState
              }
              disabled={Boolean(disabled || !isExtension)}
              onValueChange={(next) => {
                if (next === AUTOMATIC_SELECT_VALUE) {
                  onFieldModeChange?.(
                    "priorIoTPresenceState",
                    ChangeMode.Automatic,
                  );
                  return;
                }
                markManual("priorIoTPresenceState");
                update({
                  priorIoTPresenceState: next as PriorIoTPresenceState,
                });
              }}
            >
              <SelectTrigger className="h-9 w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {mode === "patch" && (
                  <SelectItem value={AUTOMATIC_SELECT_VALUE}>
                    {automaticLabel(
                      priorIoTPresenceStateLabel(value.priorIoTPresenceState),
                    )}
                  </SelectItem>
                )}
                {shouldRenderManualOption(
                  "priorIoTPresenceState",
                  value.priorIoTPresenceState,
                  PriorIoTPresenceState.Unknown,
                ) && (
                  <SelectItem value={PriorIoTPresenceState.Unknown}>
                    {t("Unbekannt")}
                  </SelectItem>
                )}
                {shouldRenderManualOption(
                  "priorIoTPresenceState",
                  value.priorIoTPresenceState,
                  PriorIoTPresenceState.Present,
                ) && (
                  <SelectItem value={PriorIoTPresenceState.Present}>
                    {t("Vorhanden")}
                  </SelectItem>
                )}
                {shouldRenderManualOption(
                  "priorIoTPresenceState",
                  value.priorIoTPresenceState,
                  PriorIoTPresenceState.Absent,
                ) && (
                  <SelectItem value={PriorIoTPresenceState.Absent}>
                    {t("Nicht vorhanden")}
                  </SelectItem>
                )}
              </SelectContent>
            </Select>,
          )}

        {showExtensionFields &&
          row(
            t("Tabelle anzeigen"),
            <Select
              value={
                isAutomatic("tableMode")
                  ? AUTOMATIC_SELECT_VALUE
                  : value.tableMode
              }
              disabled={Boolean(disabled || !isExtension)}
              onValueChange={(next) => {
                if (next === AUTOMATIC_SELECT_VALUE) {
                  onFieldModeChange?.("tableMode", ChangeMode.Automatic);
                  return;
                }
                markManual("tableMode");
                update({ tableMode: next as SectionMode });
              }}
            >
              <SelectTrigger className="h-9 w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {mode === "patch" && (
                  <SelectItem value={AUTOMATIC_SELECT_VALUE}>
                    {automaticLabel(sectionModeLabel(value.tableMode))}
                  </SelectItem>
                )}
                {shouldRenderManualOption(
                  "tableMode",
                  value.tableMode,
                  SectionMode.Include,
                ) && (
                  <SelectItem value={SectionMode.Include}>
                    {t("Anzeigen")}
                  </SelectItem>
                )}
                {shouldRenderManualOption(
                  "tableMode",
                  value.tableMode,
                  SectionMode.Omit,
                ) && (
                  <SelectItem value={SectionMode.Omit}>
                    {t("Ausblenden")}
                  </SelectItem>
                )}
              </SelectContent>
            </Select>,
          )}

        {showExtensionFields &&
          row(
            t("IoT in Tabelle"),
            <Select
              value={
                isAutomatic("ioTInTableMode")
                  ? AUTOMATIC_SELECT_VALUE
                  : value.ioTInTableMode
              }
              disabled={Boolean(disabled || !isExtension || !canEditIotInTable)}
              onValueChange={(next) => {
                if (next === AUTOMATIC_SELECT_VALUE) {
                  onFieldModeChange?.("ioTInTableMode", ChangeMode.Automatic);
                  return;
                }
                markManual("ioTInTableMode");
                update({ ioTInTableMode: next as IoTTableMode });
              }}
            >
              <SelectTrigger className="h-9 w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {mode === "patch" && (
                  <SelectItem value={AUTOMATIC_SELECT_VALUE}>
                    {automaticLabel(ioTTableModeLabel(value.ioTInTableMode))}
                  </SelectItem>
                )}
                {shouldRenderManualOption(
                  "ioTInTableMode",
                  value.ioTInTableMode,
                  IoTTableMode.Include,
                ) && (
                  <SelectItem value={IoTTableMode.Include}>
                    {t("Anzeigen")}
                  </SelectItem>
                )}
                {shouldRenderManualOption(
                  "ioTInTableMode",
                  value.ioTInTableMode,
                  IoTTableMode.Exclude,
                ) && (
                  <SelectItem value={IoTTableMode.Exclude}>
                    {t("Ausblenden")}
                  </SelectItem>
                )}
              </SelectContent>
            </Select>,
          )}

        {showExtensionFields &&
          row(
            t("IoT-Mietvertrag anzeigen"),
            <Select
              value={
                isAutomatic("ioTRentalContractMode")
                  ? AUTOMATIC_SELECT_VALUE
                  : value.ioTRentalContractMode
              }
              disabled={Boolean(disabled || !isExtension)}
              onValueChange={(next) => {
                if (next === AUTOMATIC_SELECT_VALUE) {
                  onFieldModeChange?.(
                    "ioTRentalContractMode",
                    ChangeMode.Automatic,
                  );
                  return;
                }
                markManual("ioTRentalContractMode");
                update({ ioTRentalContractMode: next as SectionMode });
              }}
            >
              <SelectTrigger className="h-9 w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {mode === "patch" && (
                  <SelectItem value={AUTOMATIC_SELECT_VALUE}>
                    {automaticLabel(
                      sectionModeLabel(value.ioTRentalContractMode),
                    )}
                  </SelectItem>
                )}
                {shouldRenderManualOption(
                  "ioTRentalContractMode",
                  value.ioTRentalContractMode,
                  SectionMode.Include,
                ) && (
                  <SelectItem value={SectionMode.Include}>
                    {t("Anzeigen")}
                  </SelectItem>
                )}
                {shouldRenderManualOption(
                  "ioTRentalContractMode",
                  value.ioTRentalContractMode,
                  SectionMode.Omit,
                ) && (
                  <SelectItem value={SectionMode.Omit}>
                    {t("Ausblenden")}
                  </SelectItem>
                )}
              </SelectContent>
            </Select>,
          )}

        {showExtensionFields &&
          row(
            t("Bezugsvertragsdatum"),
            <DateInput
              date={value.referencedContractDate}
              onDateChange={(next) => update({ referencedContractDate: next })}
              disabled={Boolean(disabled || !isExtension)}
              allowYearSelection
            />,
          )}

        {showExtensionFields &&
          showTargetOrganismSelection &&
          targetOrganismSelection &&
          onTargetOrganismSelectionChange &&
          row(
            t("Schädlingsauswahl"),
            <TargetOrganismSelectionEditor
              disabled={disabled}
              selection={targetOrganismSelection}
              onChange={onTargetOrganismSelectionChange}
            />,
            "grid content-start gap-1.5 md:col-span-2",
          )}

        {showExtensionFields &&
          showPestDisplayFields &&
          row(
            t("gegen"),
            withOverrideMode(
              "contractAgainstText",
              <ContractAgainstHtmlEditor
                value={value.contractAgainstText}
                disabled={Boolean(
                  disabled ||
                  !isExtension ||
                  isAutomatic("contractAgainstText"),
                )}
                onChange={(next) => {
                  markManual("contractAgainstText");
                  update({ contractAgainstText: next });
                }}
              />,
            ),
            "grid content-start gap-1.5 md:col-span-2",
          )}

        {showExtensionFields &&
          showPestDisplayFields &&
          row(
            t("Bereich"),
            withOverrideMode(
              "targetAreaScope",
              <Select
                value={value.targetAreaScope}
                disabled={Boolean(
                  disabled || !isExtension || isAutomatic("targetAreaScope"),
                )}
                onValueChange={(next) => {
                  markManual("targetAreaScope");
                  update({ targetAreaScope: next as ExtensionAreaScope });
                }}
              >
                <SelectTrigger className="h-9 w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ExtensionAreaScope.Indoor}>
                    {extensionAreaScopeLabel(ExtensionAreaScope.Indoor)}
                  </SelectItem>
                  <SelectItem value={ExtensionAreaScope.Outdoor}>
                    {extensionAreaScopeLabel(ExtensionAreaScope.Outdoor)}
                  </SelectItem>
                  <SelectItem value={ExtensionAreaScope.IndoorOutdoor}>
                    {extensionAreaScopeLabel(ExtensionAreaScope.IndoorOutdoor)}
                  </SelectItem>
                </SelectContent>
              </Select>,
            ),
          )}
      </div>

      {validationMessage && (
        <p className="text-destructive text-sm">{validationMessage}</p>
      )}
    </div>
  );
}
